<template>
  <div class="vimeo-embed" v-if="component.video" :style="containerStyle">
    <div
      class="vimeo-embed__spinner d-flex justify-content-center align-items-center"
      v-if="!ready"
    >
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <iframe
      v-show="ready"
      ref="iframe"
      class="vimeo-embed__frame"
      :src="embedUrl"
      frameborder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowfullscreen
      @load="onLoad"
    ></iframe>
  </div>
</template>

<script>
export default {
  props: ['component'],
  data() {
    return {
      ready: false,
      videoWidth: null,
      videoHeight: null,
    }
  },
  computed: {
    embedUrl() {
      const id = this.component.video.split('/').pop()
      return `https://player.vimeo.com/video/${id}?api=1`
    },
    // Native width / height of the video, e.g. 0.5625 for a 9:16 (portrait) video.
    // Falls back to 16:9 until the player reports the real size.
    ratio() {
      if (this.videoWidth && this.videoHeight) {
        return this.videoWidth / this.videoHeight
      }
      return 16 / 9
    },
    containerStyle() {
      const ratio = this.ratio
      return {
        aspectRatio: `${ratio}`,
        // Cap the height so a portrait video stays watchable on screen
        maxWidth: `calc(var(--vimeo-max-height) * ${ratio})`,
      }
    },
  },
  mounted() {
    if (this.component.video) {
      window.addEventListener('message', this.onMessage)
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    onLoad() {
      this.ready = true
      this.postMessage('addEventListener', 'finish')
      this.postMessage('addEventListener', 'resize')
      this.postMessage('addEventListener', 'loaded')
      this.requestDimensions()
    },
    requestDimensions() {
      this.postMessage('getVideoWidth')
      this.postMessage('getVideoHeight')
    },
    onMessage(event) {
      if (event.origin !== 'https://player.vimeo.com') return
      // Ignore messages coming from another Vimeo player on the page.
      // Without this, all instances would receive every player's dimension
      // replies and `resize`/`finish` events, so a second embed would
      // overwrite the first one's aspect ratio.
      if (!this.$refs.iframe || event.source !== this.$refs.iframe.contentWindow) return

      // the player may post a JSON string or a plain object
      let data = event.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          return
        }
      }
      if (!data) return

      // replies to getVideoWidth / getVideoHeight carry the value in `value`
      if (data.method === 'getVideoWidth') {
        this.videoWidth = Number(data.value) || this.videoWidth
        return
      }
      if (data.method === 'getVideoHeight') {
        this.videoHeight = Number(data.value) || this.videoHeight
        return
      }

      // the player reports the native media size (and again on quality changes)
      if (data.event === 'resize') {
        const { videoWidth, videoHeight } = data.data || {}
        if (videoWidth && videoHeight) {
          this.videoWidth = Number(videoWidth)
          this.videoHeight = Number(videoHeight)
        }
        return
      }

      if (data.event === 'loaded') {
        this.requestDimensions()
        return
      }

      if (data.event === 'finish') {
        this.$emit('videoEnded', { event: data, component_id: this.component.id })
      }
    },
    play() {
      this.postMessage('play')
    },
    postMessage(method, value) {
      if (!this.$refs.iframe) return
      const message = JSON.stringify({ method, value })
      this.$refs.iframe.contentWindow.postMessage(message, 'https://player.vimeo.com')
    },
  },
}
</script>